import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { find } from 'ramda';
import Button from '~~elements/Button';
import Multilingual from '~~utils/Multilingual';
import { useQuery } from '~~utils/CommonUtils';
import { SectionDiv } from '../styled';
import ScheduleCalendar from './ScheduleCalendar';
import ScheduleGroup from './ScheduleGroup';
import ScheduleLocation from './ScheduleLocation';
import ScheduleGameModal from './ScheduleGameModal';
import ScheduleCreateGameModal from './ScheduleCreateGameModal';
import moment from 'moment';

const DisplayControlDiv = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  > * {
    padding: 0.5rem 1rem;
    margin: 0 1rem 0 0;
  }
  padding-bottom: 0.2rem;
  border-bottom: 1px solid #eee;
`;

const Schedule = ({ tournament, games, groups }) => {
  const getModalGameInitState = () => {
    return {
      uniqid: '',
      seq: 0,
      group: groups.length === 0 ? 'NONE' : groups[0].name,
      away: {
        team: 'NONE',
        icon_url: '',
        scores: [],
        runs: 0,
        hits: 0,
        records: [],
      },
      home: {
        team: 'NONE',
        icon_url: '',
        scores: [],
        runs: 0,
        hits: 0,
        records: [],
      },
      info: {
        location: '',
        location_uniqid: 'NONE',
        innings: 0,
        stats_completeness: 'NONE',
        status: 'SCHEDULED',
        finished_status: '',
        time: {
          ended_at: '',
          scheduled_start_at: '',
          started_at: '',
        },
        winner_side: '',
      },
      // news: {},
      // media: [],
    };
  };

  const query = useQuery();
  const displayMode = query.get('schedule');

  const [gameLocations, setGameLocations] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDateRange, setSelectedDateRange] = useState(['', '']);

  const [gameListCollapse, setGameListCollapse] = useState(false);

  const [selectedGame, setSelectedGame] = useState('');
  const [modalGame, setModalGame] = useState(getModalGameInitState());

  const [tryCreateGame, setTryCreateGame] = useState(false);
  const [createGameDefaultValue, setCreateGameDefaultValue] = useState(getModalGameInitState());

  useEffect(() => {
    const locations = {};
    games.forEach((game) => {
      if (locations[game.info.location_uniqid]) {
        return;
      }

      locations[game.info.location_uniqid] = {
        name: game.info.location,
        uniqid: game.info.location_uniqid,
      };
    });

    setGameLocations(Object.values(locations));
  }, [games]);

  useEffect(() => {
    if (!selectedGame) {
      setModalGame(getModalGameInitState());
      return;
    }

    const targetGame = find((g) => g.uniqid === selectedGame, games);
    if (!targetGame) {
      setModalGame(getModalGameInitState());
      setSelectedGame('');
      return;
    }

    const targetGameData = { ...targetGame };
    setModalGame(targetGameData);
  }, [games, selectedGame]);

  const handleGameFilterOption = (filterType, option) => () => {
    const filters = {
      GROUP: {
        value: selectedGroups,
        setter: setSelectedGroups,
      },
      LOCATION: {
        value: selectedLocations,
        setter: setSelectedLocations,
      },
    };

    let targetFilter = filters[filterType].value.slice(0);
    switch (option) {
      case 'ALL':
        targetFilter = [];
        break;
      default: {
        const optionIndex = targetFilter.indexOf(option);
        if (optionIndex >= 0) {
          targetFilter.splice(optionIndex, 1);
          break;
        }
        targetFilter.push(option);
        break;
      }
    }

    filters[filterType].setter(targetFilter);
  };

  const handleGameFilterDateRange = (e) => {
    const name = e.target.name;
    if (name === 'all') {
      setSelectedDateRange(['', '']);
      return;
    }

    const value = new Date(e.target.value);
    if (name === 'start') {
      selectedDateRange[0] = !moment(value).isValid() ? '' : value;
    }
    if (name === 'end') {
      selectedDateRange[1] = !moment(value).isValid() ? '' : value;
    }
    setSelectedDateRange(selectedDateRange.slice(0));
  };

  const changeDisplayMode = (targetMode) => () => {
    query.set({ schedule: targetMode });
  };

  const handleTryCreateGame = (date, group, locationUniqid) => () => {
    setTryCreateGame(true);
    const initGameData = getModalGameInitState();
    if (date && moment(date).isValid()) {
      initGameData.info.time.scheduled_start_at = `${moment(date).format('YYYY/MM/DD')} 08:00`;
    }
    if (group) {
      initGameData.group = group;
    }
    if (locationUniqid) {
      initGameData.info.location_uniqid = locationUniqid;
    }

    setCreateGameDefaultValue(initGameData);
  };

  const handleCancelGameCreate = () => {
    setTryCreateGame(false);
    setCreateGameDefaultValue(getModalGameInitState());
  };

  const getDisplayMode = () => {
    const commonProps = {
      tournament,
      games,
      groups,
      gameLocations,
      handleGameFilterOption,
      setGameListCollapse,
      gameListCollapse,
      setSelectedGame,
      handleTryCreateGame,
    };

    switch (displayMode) {
      case 'game_group':
        return (
          <ScheduleGroup
            {...commonProps}
            handleGameFilterDateRange={handleGameFilterDateRange}
            selectedLocations={selectedLocations}
            selectedDateRange={selectedDateRange}
          />
        );
      case 'location':
        return (
          <ScheduleLocation
            {...commonProps}
            handleGameFilterDateRange={handleGameFilterDateRange}
            selectedGroups={selectedGroups}
            selectedDateRange={selectedDateRange}
          />
        );
      default:
        return (
          <ScheduleCalendar {...commonProps} selectedGroups={selectedGroups} selectedLocations={selectedLocations} />
        );
    }
  };

  const handleCancelGameSelect = () => {
    setModalGame(getModalGameInitState());
    setSelectedGame('');
  };

  return (
    <SectionDiv>
      <DisplayControlDiv>
        <h3>{Multilingual(`TOURNAMENT.SCHEDULE_DISPLAY_MODE`)}</h3>
        <Button
          width="unset"
          color={displayMode !== 'calendar' ? 'transparentWithBorder' : 'red'}
          nowrap
          onClick={changeDisplayMode('calendar')}
        >
          {Multilingual(`CALENDAR.WORDING`)}
        </Button>
        <Button
          width="unset"
          color={displayMode !== 'game_group' ? 'transparentWithBorder' : 'red'}
          nowrap
          onClick={changeDisplayMode('game_group')}
        >
          {Multilingual(`TOURNAMENT.GAME_GROUP`)}
        </Button>
        <Button
          width="unset"
          color={displayMode !== 'location' ? 'transparentWithBorder' : 'red'}
          nowrap
          onClick={changeDisplayMode('location')}
        >
          {Multilingual(`TOURNAMENT.LOCATION`)}
        </Button>
      </DisplayControlDiv>
      {getDisplayMode()}
      <ScheduleGameModal
        tournament={tournament}
        selectedGame={selectedGame}
        handleCancelGameSelect={handleCancelGameSelect}
        modalGame={modalGame}
      />
      <ScheduleCreateGameModal
        tryCreateGame={tryCreateGame}
        createGameDefaultValue={createGameDefaultValue}
        handleCancelGameCreate={handleCancelGameCreate}
        setSelectedGame={setSelectedGame}
      />
    </SectionDiv>
  );
};

export default Schedule;
